import {
  useState,
  useRef,
  useCallback,
  useMemo,
  useEffect,
  type KeyboardEvent,
  type ClipboardEvent,
  type ChangeEvent,
  type DragEvent,
  type MouseEvent,
} from "react";
import {
  AttachmentChip,
  Dropzone,
  Icon,
  Menu,
  MenuItem,
} from "@fwf/fwfa-design-system/react";
import type {
  ConnectionStatus,
  PromptAttachment,
  PromptPayload,
  SlashCommand,
} from "@/types";
import {
  extractFilesFromTransfer,
  mergePromptAttachments,
} from "@/lib/prompt-attachments";
import { GlassOrb } from "./GlassOrb";
import { FileGlyph } from "./AttachmentGlyphs";

const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

function fileToAttachment(file: File): Promise<PromptAttachment> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(`Failed to read ${file.name || "attachment"}`));
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error(`Failed to load ${file.name || "attachment"}`));
        return;
      }

      const commaIndex = result.indexOf(",");
      if (commaIndex === -1) {
        reject(new Error(`Invalid attachment data for ${file.name || "attachment"}`));
        return;
      }

      const inferredKind = file.type.startsWith("image/") ? "image" : "file";
      const extension = file.type.startsWith("image/")
        ? (file.type.split("/")[1] || "png")
        : "bin";

      resolve({
        id: crypto.randomUUID(),
        name: file.name || `attachment-${Date.now()}.${extension}`,
        mimeType: file.type || "application/octet-stream",
        size: file.size,
        kind: inferredKind,
        data: result.slice(commaIndex + 1),
      });
    };
    reader.readAsDataURL(file);
  });
}

interface Props {
  onSend: (payload: PromptPayload) => void;
  onStop: () => void;
  onForceStop: () => void;
  disabled: boolean;
  isStreaming: boolean;
  isCancelling: boolean;
  agentStatus?: ConnectionStatus;
  availableCommands?: SlashCommand[];
  uiVariant?: "desktop" | "web";
}

export function InputArea({
  onSend,
  onStop,
  onForceStop,
  disabled,
  isStreaming,
  isCancelling,
  agentStatus,
  availableCommands = [],
  uiVariant = "desktop",
}: Props) {
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState<PromptAttachment[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [isReadingAttachments, setIsReadingAttachments] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showQuickActions, setShowQuickActions] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const quickActionsRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragDepthRef = useRef(0);
  const [isDragActive, setIsDragActive] = useState(false);

  // Determine if the user is typing a slash command (cursor in the first word starting with /)
  const commandFilter = useMemo(() => {
    if (!text.startsWith("/") || availableCommands.length === 0) return null;
    // Only show menu while still typing the command name (no space yet)
    const spaceIdx = text.indexOf(" ");
    if (spaceIdx !== -1) return null;
    return text.slice(1).toLowerCase();
  }, [text, availableCommands]);

  const filteredCommands = useMemo(() => {
    if (commandFilter === null) return [];
    return availableCommands.filter((cmd) =>
      cmd.name.toLowerCase().startsWith(commandFilter),
    );
  }, [commandFilter, availableCommands]);

  const showMenu = filteredCommands.length > 0;

  // Set correct initial height (rows=1 default is slightly taller than leading-5)
  useEffect(() => {
    handleInput();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Refocus textarea when it becomes enabled again (e.g. after agent response)
  useEffect(() => {
    if (!disabled) {
      textareaRef.current?.focus();
    }
  }, [disabled]);

  useEffect(() => {
    function handleClick(e: globalThis.MouseEvent) {
      if (quickActionsRef.current && !quickActionsRef.current.contains(e.target as Node)) {
        setShowQuickActions(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Reset selection when filter changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [commandFilter]);

  // Scroll selected item into view. The ref is on the positioning wrapper, so
  // the rows are queried by class rather than indexed off it directly; Menu is
  // the scroll container and `nearest` finds it either way.
  useEffect(() => {
    if (!showMenu || !menuRef.current) return;
    const items = menuRef.current.querySelectorAll<HTMLElement>(".fwfa-menu-item");
    items[selectedIndex]?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex, showMenu]);

  const selectCommand = useCallback(
    (cmd: SlashCommand) => {
      setText(`/${cmd.name} `);
      requestAnimationFrame(() => {
        handleInput();
      });
      textareaRef.current?.focus();
      setShowQuickActions(false);
    },
    [],
  );

  const appendFiles = useCallback(async (files: File[]) => {
    if (files.length === 0) return;

    setAttachmentError(null);
    setIsReadingAttachments(true);

    try {
      for (const file of files) {
        if (file.size > MAX_ATTACHMENT_BYTES) {
          throw new Error(`${file.name || "Attachment"} exceeds the 10 MB limit.`);
        }
      }

      const loaded = await Promise.all(files.map((file) => fileToAttachment(file)));
      setAttachments((prev) => mergePromptAttachments(prev, loaded));
    } catch (err) {
      setAttachmentError(err instanceof Error ? err.message : "Failed to attach file");
    } finally {
      setIsReadingAttachments(false);
    }
  }, []);

  const removeAttachment = useCallback((attachmentId: string) => {
    setAttachments((prev) => prev.filter((attachment) => attachment.id !== attachmentId));
  }, []);

  const focusTextarea = useCallback(() => {
    textareaRef.current?.focus();
  }, []);

  const handleFileChange = useCallback(async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    await appendFiles(files);
    event.target.value = "";
  }, [appendFiles]);

  const handlePaste = useCallback(async (event: ClipboardEvent<HTMLTextAreaElement>) => {
    const files = extractFilesFromTransfer(event.clipboardData);

    if (files.length === 0) return;
    event.preventDefault();
    await appendFiles(files);
  }, [appendFiles]);

  const handleDragEnter = useCallback((event: DragEvent<HTMLDivElement>) => {
    const files = extractFilesFromTransfer(event.dataTransfer);
    if (files.length === 0) return;
    event.preventDefault();
    dragDepthRef.current += 1;
    setIsDragActive(true);
  }, []);

  const handleDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
    const files = extractFilesFromTransfer(event.dataTransfer);
    if (files.length === 0) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
    if (!isDragActive) setIsDragActive(true);
  }, [isDragActive]);

  const handleDragLeave = useCallback((event: DragEvent<HTMLDivElement>) => {
    const files = extractFilesFromTransfer(event.dataTransfer);
    if (files.length === 0) return;
    event.preventDefault();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) {
      setIsDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(async (event: DragEvent<HTMLDivElement>) => {
    const files = extractFilesFromTransfer(event.dataTransfer);
    if (files.length === 0) return;
    event.preventDefault();
    dragDepthRef.current = 0;
    setIsDragActive(false);
    await appendFiles(files);
    focusTextarea();
  }, [appendFiles, focusTextarea]);

  const handleComposerClick = useCallback((event: MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement | null;
    if (target?.closest("button, input, textarea, a")) return;
    focusTextarea();
  }, [focusTextarea]);

  const canSend = (text.trim().length > 0 || attachments.length > 0) && !disabled && !isReadingAttachments;

  const handleSend = useCallback(() => {
    if (!canSend) return;
    onSend({
      text,
      attachments,
    });
    setText("");
    setAttachments([]);
    setAttachmentError(null);
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  }, [attachments, canSend, onSend, text]);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (showMenu) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, filteredCommands.length - 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.key === "Tab" || (e.key === "Enter" && !e.shiftKey)) {
        e.preventDefault();
        selectCommand(filteredCommands[selectedIndex]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setText("");
        return;
      }
    }

    if (showQuickActions && e.key === "Escape") {
      e.preventDefault();
      setShowQuickActions(false);
      return;
    }

    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInput = () => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = "auto";
      const capped = Math.min(el.scrollHeight, 200);
      el.style.height = capped + "px";
      el.style.overflowY = el.scrollHeight > 200 ? "auto" : "hidden";
    }
  };

  const handleStopClick = useCallback(() => {
    if (isCancelling) {
      onForceStop();
    } else {
      onStop();
    }
  }, [isCancelling, onStop, onForceStop]);

  return (
    <div className="bg-white dark:bg-transparent px-4 pt-1 pb-5">
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileChange}
        className="hidden"
      />

      <div className="mx-auto max-w-3xl space-y-3">
        {attachments.length > 0 && (
          <div className="ph-no-capture fwfa-attachment-list" data-private="prompt-attachments">
            {attachments.map((attachment) => (
              <AttachmentChip
                key={attachment.id}
                name={attachment.name}
                thumbnailSrc={
                  attachment.kind === "image"
                    ? `data:${attachment.mimeType};base64,${attachment.data}`
                    : undefined
                }
                icon={attachment.kind === "image" ? undefined : <FileGlyph />}
                onRemove={() => removeAttachment(attachment.id)}
              />
            ))}
          </div>
        )}

        {attachmentError && (
          <div className="ph-no-capture text-xs text-brand-red" data-private="attachment-error">{attachmentError}</div>
        )}

        <div
          className="relative rounded-2xl"
          ref={quickActionsRef}
          onClick={handleComposerClick}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {showQuickActions && (
            <Menu
              label="Quick actions"
              className="absolute bottom-full left-0 z-10 mb-2 w-64"
            >
              <MenuItem
                onClick={() => {
                  setShowQuickActions(false);
                  fileInputRef.current?.click();
                }}
              >
                <span className="flex items-center gap-3">
                  <Icon aria-hidden="true" className="text-ink-muted">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21.44 11.05l-8.49 8.49a5.5 5.5 0 01-7.78-7.78l9.2-9.19a3.5 3.5 0 114.95 4.95l-9.19 9.2a1.5 1.5 0 01-2.12-2.13l8.49-8.48" />
                    </svg>
                  </Icon>
                  Add files or photos
                </span>
              </MenuItem>

              {availableCommands.length > 0 && (
                <MenuItem
                  onClick={() => {
                    setShowQuickActions(false);
                    setText("/");
                    textareaRef.current?.focus();
                    requestAnimationFrame(() => handleInput());
                  }}
                >
                  <span className="flex items-center gap-3">
                    <Icon aria-hidden="true" className="font-mono text-ink-muted">
                      /
                    </Icon>
                    Slash commands
                  </span>
                </MenuItem>
              )}
            </Menu>
          )}

          {/* Slash command autocomplete. The menu is a listbox rather than a
              menu: focus stays in the textarea and the arrow keys move an active
              option, so each row carries aria-selected — which is also what
              draws the DS tick on the row Enter will take. The ref sits on the
              positioning wrapper because DS wrappers are typed
              ComponentPropsWithoutRef and cannot take one. */}
          {showMenu && (
            <div
              ref={menuRef}
              className="absolute bottom-full left-0 right-0 z-10 mb-2"
            >
              <Menu role="listbox" label="Slash commands">
                {filteredCommands.map((cmd, i) => (
                  <MenuItem
                    key={cmd.name}
                    role="option"
                    aria-selected={i === selectedIndex}
                    onMouseDown={(e) => {
                      e.preventDefault(); // keep focus on textarea
                      selectCommand(cmd);
                    }}
                    onMouseEnter={() => setSelectedIndex(i)}
                  >
                    <span className="flex min-w-0 items-baseline gap-3">
                      <span className="shrink-0 font-mono text-sm">/{cmd.name}</span>
                      <span className="truncate text-xs text-ink-muted">
                        {cmd.description}
                      </span>
                    </span>
                  </MenuItem>
                ))}
              </Menu>
            </div>
          )}

          <button
            type="button"
            onClick={() => {
              setShowQuickActions((prev) => !prev);
            }}
            disabled={disabled || isReadingAttachments}
            className="group/plus absolute bottom-4 left-4 z-[1] flex h-8 w-8 items-center justify-center text-stone-400 transition-colors hover:text-gold disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Attach files or run commands"
          >
            <svg className={`h-4 w-4 transition-transform duration-200 ${showQuickActions ? "rotate-45" : ""}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14M5 12h14" />
            </svg>
            {!showQuickActions && (
              <span className="pointer-events-none absolute left-10 whitespace-nowrap rounded-md bg-navy-dark px-2.5 py-1.5 text-xs font-medium text-stone-200 opacity-0 shadow-lg ring-1 ring-white/10 transition-opacity group-hover/plus:opacity-100">
                Attach files or commands
              </span>
            )}
          </button>

          {/* Send / Stop button — inline, bottom-right of textarea */}
          {isStreaming ? (() => {
            const agentGone = agentStatus === "disconnected" || agentStatus === "error";
            return (
              <button
                onClick={agentGone ? undefined : handleStopClick}
                disabled={agentGone}
                className={`absolute bottom-4 right-4 z-[1] flex h-8 w-8 items-center justify-center rounded-full transition-all active:translate-y-px ${
                  agentGone
                    ? "cursor-not-allowed bg-stone-700 text-stone-500"
                    : isCancelling
                      ? "bg-brand-red text-white hover:bg-red-700"
                      : "bg-white/[0.08] text-stone-400 hover:bg-white/[0.14] hover:text-white"
                }`}
                title={agentGone ? "Agent disconnected" : isCancelling ? "Force stop agent" : "Stop generation"}
              >
                {isCancelling && !agentGone ? (
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                ) : (
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                    <rect x="6" y="6" width="12" height="12" rx="2" />
                  </svg>
                )}
              </button>
            );
          })() : (
            <GlassOrb
              tone={canSend ? "gold" : "neutral"}
              size="sm"
              onClick={handleSend}
              disabled={!canSend}
              className="!absolute bottom-4 right-4 z-[1]"
              title="Send message"
              aria-label="Send message"
            >
              <svg
                className="h-3.5 w-3.5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 10.5 12 3m0 0 7.5 7.5M12 3v18" />
              </svg>
            </GlassOrb>
          )}

          <textarea
            ref={textareaRef}
            data-private="prompt-input"
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              handleInput();
            }}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder={
              disabled
                ? "Waiting for response..."
                : availableCommands.length > 0
                  ? "Type a message, paste files, or / for commands..."
                  : "Type a message or paste files..."
            }
            disabled={disabled}
            rows={1}
            className={`ph-no-capture min-h-[76px] w-full resize-none overflow-hidden rounded-2xl border border-stone-300/70 dark:border-white/[0.06] bg-stone-50 dark:bg-[#031f2b]/90 pl-14 pr-12 pt-4 pb-11 font-body leading-6 text-navy dark:text-stone-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] focus:border-fwf-yellow/20 focus:outline-none focus:ring-1 focus:ring-fwf-yellow/10 disabled:opacity-50 placeholder-stone-400 dark:placeholder-stone-500 ${
              uiVariant === "desktop" ? "text-base" : "text-[0.875rem]"
            }`}
          />

          {/* Last child, so it covers the composer controls it shares a layer
              with. It stays mounted and fades: the DS overlay hides itself from
              the screen and the a11y tree at rest, and is pointer-transparent so
              the drag handlers on this wrapper keep firing. */}
          <Dropzone overlay dragging={isDragActive}>
            Drop screenshots or files to attach
          </Dropzone>
        </div>
      </div>
    </div>
  );
}
